// Home.js — authenticated landing: ensures the four lists (S4) then
// presents the board (S5): blocks as the drag source, one column per
// canonical list, drop / quick-add persists a listitem with createdAt.

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Board from './Board';
import DenseBoard from './DenseBoard';
import ActionLog from './ActionLog';
import { LIST_DEFS, ensureListsOnAccount } from '../lib/lists';
import {
  addListItem,
  deleteBlockRecord,
  fetchAllBlocks,
  fetchCanonicalLists,
  fetchListItems,
  findDeletedDids,
  itemsContainDid,
  pardonAll,
  removeListItem,
  unblockAccount,
} from '../lib/board';
import { fetchListblocks, setListMode } from '../lib/modes';

const emptyColumns = () =>
  LIST_DEFS.map((def) => ({ name: def.name, uri: null, items: [], loading: true }));

// Session-scoped action log (refinement 2026-09-07): every write the app
// performs is recorded with its at-uri so the owner can verify records on
// atproto.at / pdsls.dev.
let actionSeq = 0;

const newAction = (label, uri) => ({
  id: ++actionSeq,
  ts: new Date().toISOString(),
  label,
  uri: uri ?? null,
});

const Home = () => {
  const { session, agent, logout } = useAuth();
  const { showToast } = useToast();
  const [phase, setPhase] = useState('loading'); // loading|ready|error
  const [errorMsg, setErrorMsg] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [columns, setColumns] = useState(emptyColumns);
  const [modes, setModes] = useState({}); // listName -> 'mute'|'block'|null
  const [actions, setActions] = useState([]); // session action log
  const [deletedDids, setDeletedDids] = useState([]); // Feature 1: deleted block subjects
  const [layout, setLayout] = useState('board'); // Feature 2: 'board'|'dense'
  const [pardonConfirming, setPardonConfirming] = useState(false); // Pardon: confirm state
  const [pardonProgress, setPardonProgress] = useState(null); // Pardon: status line
  const [busy, setBusy] = useState(false);
  const ranRef = useRef(false);

  const userDid = session?.did ?? session?.sub;
  const log = (label, uri) => setActions((prev) => [newAction(label, uri), ...prev]);

  // One full board load per mount (guard StrictMode double-effect).
  useEffect(() => {
    if (!agent || !userDid || ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const ensureResult = await ensureListsOnAccount(agent, userDid);

        // Refinement 2026-09-07: record created lists in the action log so
        // the owner can verify them (each carries its record at-uri).
        for (const created of ensureResult.created) {
          log(`Created list ${created.name}`, created.uri);
        }

        const canonical = await fetchCanonicalLists(agent, userDid);

        if (canonical.length !== LIST_DEFS.length) {
          throw new Error(
            `Expected ${LIST_DEFS.length} canonical lists, found ${canonical.length}.`
          );
        }

        const [blockProfiles, ...perList] = await Promise.all([
          fetchAllBlocks(agent),
          ...canonical.map((l) => fetchListItems(agent, l.uri)),
        ]);

        setBlocks(blockProfiles);

        // Feature 1: flag block records whose subject account no longer
        // resolves (deleted accounts) so the user can delete the stale
        // block record.
        try {
          const dead = await findDeletedDids(blockProfiles.map((b) => b.did));
          setDeletedDids(dead);

          if (dead.length > 0) {
            log(
              `Found ${dead.length} deleted account${dead.length > 1 ? 's' : ''} among your blocks`,
              null
            );
          }
        } catch (deletedErr) {
          console.error('Deleted-DID check failed:', deletedErr);
        }

        setColumns(
          canonical.map((l, i) => ({
            ...l,
            items: perList[i],
            loading: false,
          }))
        );
        // Detect current enforcement (S6): listblock records (public) +
        // each column list view's own muted state.
        const listblocks = await fetchListblocks(agent, userDid);
        const nextModes = {};

        for (const col of canonical) {
          const li = perList[canonical.indexOf(col)];
          const blockedRec = listblocks.find((b) => b.listUri === col.uri);
          const isMuted = Boolean(li?.viewer?.muted);
          nextModes[col.name] = blockedRec ? 'block' : isMuted ? 'mute' : null;
        }

        setModes(nextModes);
        setPhase('ready');
      } catch (err) {
        console.error('Board load failed:', err);
        setErrorMsg(String(err?.message ?? err));
        setPhase('error');
      }
    })();
  }, [agent, userDid]);

  const handleAdd = async (block, listName) => {
    if (!agent || !userDid) return;
    const col = columns.find((c) => c.name === listName);

    if (!col?.uri) {
      showToast(`List ${listName} is not ready yet.`);

      return;
    }

    if (itemsContainDid(col.items, block.did)) {
      showToast(`${block.handle || block.did} is already in ${listName}.`);

      return;
    }

    setBusy(true);

    try {
      const created = await addListItem(agent, userDid, col.uri, block.did, block);
      log(`Added ${block.handle || block.did} to ${listName}`, created.uri);
      // Reflect the add locally (profile from the block row).
      setColumns((prev) =>
        prev.map((c) =>
          c.name === listName
            ? { ...c, items: [{ uri: created.uri, subject: block }, ...c.items] }
            : c
        )
      );

      // Refinement 2026-09-07: placing on a list replaces the individual
      // block — the moderation list takes over. Unblock and drop from the
      // blocks pane.
      if (block.blockUri) {
        try {
          await unblockAccount(agent, userDid, block.blockUri);
          setBlocks((prev) => prev.filter((b) => b.did !== block.did));
          log(
            `Unblocked ${block.handle || block.did} (list membership takes over)`,
            block.blockUri
          );
        } catch (unblockErr) {
          console.error('Unblock failed:', unblockErr);
          showToast(
            `Added to ${listName}, but could not unblock ${block.handle || block.did}.`
          );
        }
      }

      showToast(`Added ${block.handle || block.did} to ${listName}.`);
    } catch (err) {
      console.error('Add failed:', err);
      showToast(`Could not add to ${listName}.`);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (item, listName) => {
    if (!agent || !userDid) return;
    setBusy(true);

    try {
      await removeListItem(agent, userDid, item.uri);
      setColumns((prev) =>
        prev.map((c) =>
          c.name === listName
            ? { ...c, items: c.items.filter((i) => i.uri !== item.uri) }
            : c
        )
      );
      log(
        `Removed ${item.subject?.handle || item.subject?.did} from ${listName}`,
        item.uri
      );
      showToast(`Removed ${item.subject?.handle || item.subject?.did} from ${listName}.`);
    } catch (err) {
      console.error('Remove failed:', err);
      showToast(`Could not remove from ${listName}.`);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteBlockRecord = async (block) => {
    if (!agent || !userDid || !block.blockUri) return;
    setBusy(true);

    try {
      await deleteBlockRecord(agent, userDid, block.blockUri);
      setBlocks((prev) => prev.filter((b) => b.did !== block.did));
      setDeletedDids((prev) => prev.filter((d) => d !== block.did));
      log(`Deleted block record for deleted account ${block.did}`, block.blockUri);
      showToast(`Deleted stale block record (${block.handle || block.did}).`);
    } catch (err) {
      console.error('Delete block record failed:', err);
      showToast(`Could not delete block record for ${block.did}.`);
    } finally {
      setBusy(false);
    }
  };

  const handlePardon = async () => {
    if (!agent || !userDid) return;

    if (!pardonConfirming) {
      setPardonConfirming(true); // forced confirmation

      return;
    }

    setPardonConfirming(false);
    setBusy(true);
    setPardonProgress('Starting pardon...');

    try {
      const result = await pardonAll(agent, userDid, {
        onProgress: (p) => setPardonProgress(p),
      });

      log(
        `PARDON: removed ${result.blocksDeleted} blocks + ${result.itemsDeleted} list memberships`,
        null
      );
      setBlocks([]);
      setColumns(emptyColumns());
      setDeletedDids([]);
      setPardonProgress(`Done — ${result.blocksDeleted} blocks and ${result.itemsDeleted} list memberships removed.`);
      showToast('Pardon complete. Redirecting to bsky.app to delete the lists.');
      // After all accounts are updated, direct the user to delete the
      // moderation lists themselves.
      window.setTimeout(() => {
        window.location.href = 'https://bsky.app/moderation/modlists';
      }, 1200);
    } catch (err) {
      console.error('Pardon failed:', err);
      setPardonProgress(null);
      showToast('Pardon failed — see console.');
      setBusy(false);
    }
  };

  const handleModeChange = async (col, next) => {
    if (!agent || !userDid || !col.uri) return;
    setBusy(true);

    try {
      await setListMode(agent, userDid, col.uri, next);
      setModes((prev) => ({ ...prev, [col.name]: next }));

      const label =
        next === null
          ? `${col.name}: enforcement off`
          : `${col.name}: ${next === 'mute' ? 'MUTED' : 'BLOCKED'} as a list`;

      log(label, col.uri);
      showToast(`${label} — all members affected.`);
    } catch (err) {
      console.error('Mode change failed:', err);
      showToast(`Could not set ${col.name} to ${next ?? 'none'}.`);
    } finally {
      setBusy(false);
    }
  };

  const handle = userDid ?? 'unknown';

  return (
    <div className="home">
      <header className="topbar">
        <span className="brand">timer</span>
        <div className="topbar-actions">
          <button
            type="button"
            className={`layout-toggle${layout === 'dense' ? ' active' : ''}`}
            onClick={() => setLayout((l) => (l === 'board' ? 'dense' : 'board'))}
            disabled={phase !== 'ready'}
          >
            {layout === 'board' ? 'Dense view' : 'Board view'}
          </button>
          <button type="button" className="link-button" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>
      {/* Pardon: single-line section under the header (destructive reset) */}
      <div className="pardon-bar">
        <button
          type="button"
          className={`pardon-btn${pardonConfirming ? ' confirming' : ''}`}
          onClick={handlePardon}
          disabled={busy || phase !== 'ready'}
        >
          {pardonConfirming
            ? 'Confirm pardon?'
            : 'Pardon'}
        </button>
        <span className="pardon-desc">
          unmute, unblock, and delete moderation lists
        </span>
        {pardonProgress && <span className="pardon-progress">{pardonProgress}</span>}
      </div>
      <main className="home-content home-board">
        <p className="muted">
          Signed in as <strong>{handle}</strong>. Drag a block into a list to
          start its timer.
        </p>

        {phase === 'loading' && <p className="status-line">Loading your blocks...</p>}
        {phase === 'error' && (
          <p className="error-text">
            Could not load your board: {errorMsg}
          </p>
        )}
        {deletedDids.length > 0 && (
          <section className="deleted-banner" aria-label="Deleted accounts">
            <p>
              <strong>{deletedDids.length}</strong> of your blocked accounts
              no longer exist (deleted). Delete their stale block records to
              clean up:
            </p>
            <ul className="deleted-list">
              {blocks
                .filter((b) => deletedDids.includes(b.did))
                .map((b) => (
                  <li key={b.did}>
                    <span>{b.handle || b.did}</span>
                    <button
                      type="button"
                      className="delete-record-btn"
                      onClick={() => handleDeleteBlockRecord(b)}
                      disabled={busy}
                    >
                      Delete record
                    </button>
                  </li>
                ))}
            </ul>
          </section>
        )}
        {phase === 'ready' &&
          (layout === 'board' ? (
            <Board
              blocks={blocks}
              columns={columns}
              modes={modes}
              onAdd={handleAdd}
              onModeChange={handleModeChange}
              onRemove={handleRemove}
              busy={busy}
            />
          ) : (
            <DenseBoard
              blocks={blocks}
              columns={columns}
              onAdd={handleAdd}
              onRemove={handleRemove}
              busy={busy}
            />
          ))}
        <ActionLog actions={actions} />
      </main>
    </div>
  );
};

export default Home;
